import React, { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

interface LocationsMenuProps {
  home?: boolean;
  mobile?: boolean;
  enabled?: boolean;
  onNavigate?: () => void;
}

export default function LocationsMenu({ home = false, mobile = false, enabled = true, onNavigate }: LocationsMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pendingFocus = useRef<number | null>(null);
  const listId = useId();

  useEffect(() => {
    if (!enabled) setOpen(false);
  }, [enabled]);

  useEffect(() => {
    if (!open) return;
    if (pendingFocus.current !== null) {
      rootRef.current?.querySelectorAll<HTMLAnchorElement>("a")[pendingFocus.current]?.focus();
      pendingFocus.current = null;
    }
    const closeOutside = (event: Event) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("focusin", closeOutside);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("focusin", closeOutside);
    };
  }, [open]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    const links = Array.from(rootRef.current?.querySelectorAll<HTMLAnchorElement>("a") || []);
    const onTrigger = event.target === buttonRef.current;
    if (onTrigger && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      const index = event.key === "ArrowDown" ? 0 : links.length - 1;
      if (open) links[index]?.focus();
      else {
        pendingFocus.current = index;
        setOpen(true);
      }
    } else if (open && !onTrigger) {
      const current = document.activeElement instanceof HTMLAnchorElement
        ? links.indexOf(document.activeElement)
        : -1;
      let next: number;
      if (event.key === "ArrowDown") next = (current + 1) % links.length;
      else if (event.key === "ArrowUp") next = (current + links.length - 1) % links.length;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = links.length - 1;
      else return;
      event.preventDefault();
      links[next]?.focus();
    }
  };

  const navigate = () => {
    setOpen(false);
    onNavigate?.();
  };
  const locationLink = (id: string, label: string) => home
    ? <a href={`#${id}`} onClick={navigate}>{label}</a>
    : <Link to={`/#${id}`} onClick={navigate}>{label}</Link>;

  return (
    <div className={`v2-locations ${mobile ? "is-mobile" : ""}`} ref={rootRef} onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        className={`v2-locations-toggle ${mobile ? "m-link" : ""}`}
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        disabled={!enabled}
        onClick={() => setOpen((value) => !value)}
      >
        <span>Locations</span>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="m4 6 4 4 4-4" />
        </svg>
      </button>
      <ul id={listId} className="v2-locations-list" hidden={!open}>
        <li>{locationLink("visit", "Brittmoore")}</li>
        <li>{locationLink("heights", "Heights · Coming soon")}</li>
      </ul>
    </div>
  );
}
